import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import ParentStatusBar from "@/components/parent/ParentStatusBar";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    CreditCard,
    CheckCircle2,
    AlertCircle,
    ExternalLink,
    Receipt,
    Calendar,
    Layers,
    Ticket,
    Wallet,
    Award,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CancelSubscriptionButton from "@/components/billing/CancelSubscriptionButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatMoney(cents, currency = "usd") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);
}

const SUB_STATUS_META = {
    active:        { label: "Active",   className: "border-brand-navy-600 text-brand-navy-200" },
    trialing:      { label: "Trialing", className: "border-brand-navy-600 text-brand-navy-200" },
    past_due:      { label: "Past due", className: "border-amber-500 text-amber-400" },
    incomplete:    { label: "Incomplete", className: "border-amber-500 text-amber-400" },
    canceled:      { label: "Canceled", className: "border-zinc-700 text-zinc-300" },
    unpaid:        { label: "Unpaid",   className: "border-brand-red text-brand-red" },
};

export default function MemberBilling() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");

    const [plans, setPlans] = useState([]);
    const [packs, setPacks] = useState([]);
    const [credits, setCredits] = useState({ credits: [], active_count: 0, total_remaining: 0 });
    const [commitments, setCommitments] = useState([]);
    const [rollover, setRollover] = useState({ credits: [], available_cents: 0 });
    const [billing, setBilling] = useState(null);
    const [charges, setCharges] = useState([]);
    const [busy, setBusy] = useState(true);
    const [checkoutBusy, setCheckoutBusy] = useState(null); // plan_id or pack_id while redirecting
    const [pollState, setPollState] = useState(null); // null | "polling" | "paid" | "failed"

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
    }, [loading, user, navigate]);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [plansRes, packsRes, meRes, creditsRes, commitsRes, rollRes, chargesRes] = await Promise.all([
                axios.get(`${API}/billing/plans`, { withCredentials: true }),
                axios.get(`${API}/billing/packs`, { withCredentials: true }),
                axios.get(`${API}/billing/me`, { withCredentials: true }),
                axios.get(`${API}/billing/me/credits`, { withCredentials: true }),
                axios.get(`${API}/me/commitments`, { withCredentials: true }).catch(() => ({ data: { commitments: [] } })),
                axios.get(`${API}/me/rollover-credits`, { withCredentials: true }).catch(() => ({ data: { credits: [], available_cents: 0 } })),
                axios.get(`${API}/billing/me/charges`, { withCredentials: true }).catch(() => ({ data: { charges: [] } })),
            ]);
            setPlans(plansRes.data || []);
            setPacks(packsRes.data || []);
            setBilling(meRes.data || null);
            setCredits(creditsRes.data || { credits: [], active_count: 0, total_remaining: 0 });
            setCommitments(commitsRes.data?.commitments || []);
            setRollover(rollRes.data || { credits: [], available_cents: 0 });
            setCharges(chargesRes.data?.charges || []);
        } catch (e) {
            toast.error("Couldn't load billing", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => { if (user) load(); }, [user, load]);

    // Polling: when redirected back from Stripe with session_id, confirm payment status
    useEffect(() => {
        if (!sessionId || !user) return;
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 6;
        setPollState("polling");

        const poll = async () => {
            attempts += 1;
            try {
                const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
                if (cancelled) return;
                if (r.data.payment_status === "paid" || r.data.payment_status === "no_payment_required") {
                    setPollState("paid");
                    toast.success("Subscription active!");
                    // Drop session_id from URL & reload billing data
                    setSearchParams({}, { replace: true });
                    load();
                    return;
                }
                if (r.data.status === "expired") {
                    setPollState("failed");
                    toast.error("Checkout expired — please try again");
                    setSearchParams({}, { replace: true });
                    return;
                }
                if (attempts >= maxAttempts) {
                    setPollState("failed");
                    toast.warning("Couldn't confirm payment yet", { description: "If charged, your subscription will appear shortly." });
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                if (!cancelled) {
                    setPollState("failed");
                    toast.error("Couldn't check payment", { description: e?.response?.data?.detail });
                }
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    // Stripe "Add card" Checkout flow returns with ?card_added=1 — surface
    // a success toast and refresh the billing payload so the panel shows
    // "Card on file ✓" right away.
    useEffect(() => {
        if (searchParams.get("card_added") !== "1" || !user) return;
        toast.success("Card on file added", {
            description: "Your coach can charge packs to this card when needed.",
        });
        // Drop the query param + reload billing data
        const url = new URL(window.location.href);
        url.searchParams.delete("card_added");
        window.history.replaceState({}, "", url.toString());
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const openPortal = async () => {
        try {
            const r = await axios.post(
                `${API}/billing/portal`,
                { return_url: `${window.location.origin}/dashboard/billing` },
                { withCredentials: true },
            );
            window.location.href = r.data.url;
        } catch (e) {
            toast.error("Couldn't open billing portal", { description: e?.response?.data?.detail });
        }
    };

    const addCard = async () => {
        setCheckoutBusy("setup-card");
        try {
            const r = await axios.post(
                `${API}/billing/setup-card`,
                { origin_url: window.location.origin },
                { withCredentials: true },
            );
            window.location.href = r.data.url;
        } catch (e) {
            toast.error("Couldn't open card setup", { description: e?.response?.data?.detail });
            setCheckoutBusy(null);
        }
    };

    if (loading || busy) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
    }

    const sub = billing?.subscription;
    const subMeta = sub ? (SUB_STATUS_META[sub.status] || { label: sub.status, className: "border-zinc-700 text-zinc-300" }) : null;
    const hasActiveSub = sub && (sub.status === "active" || sub.status === "trialing");

    return (
        <div data-testid="member-billing" className="min-h-screen bg-black text-white">
            <ParentStatusBar />
            <div className="max-w-5xl mx-auto px-4 pt-28 pb-10">
                <Link to="/dashboard" className="inline-flex items-center gap-1 text-zinc-200 hover:text-white mb-6">
                    <ArrowLeft className="w-4 h-4" /> Dashboard
                </Link>

                <h1 className="font-display text-5xl md:text-6xl tracking-wider mb-2">BILLING</h1>
                <p className="text-sm text-zinc-300 mb-8">Manage your training subscription.</p>

                {pollState === "polling" && (
                    <div data-testid="checkout-polling" className="mb-6 border border-brand-navy-700 bg-brand-navy-950/50 rounded-lg p-4 flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-brand-navy-300" />
                        <span className="text-sm">Confirming your payment with Stripe…</span>
                    </div>
                )}

                {/* Current subscription */}
                {sub && (
                    <div data-testid="current-subscription" className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6 mb-8">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="text-[11px] tracking-wider uppercase text-zinc-300">Current plan</div>
                                <div className="font-display text-3xl tracking-wider mt-1">{sub.plan_name || "Plan"}</div>
                                {subMeta && (
                                    <span className={`mt-2 inline-block text-[10px] tracking-wider uppercase px-2 py-1 border ${subMeta.className}`}>
                                        {subMeta.label}{sub.cancel_at_period_end ? " · cancelling" : ""}
                                    </span>
                                )}
                            </div>
                            <button type="button" data-testid="open-portal-btn" onClick={openPortal} className="btn-tactical">
                                <ExternalLink className="w-4 h-4" /> Manage billing
                            </button>
                        </div>
                        {sub.current_period_end && (
                            <div className="text-sm text-zinc-300 inline-flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {sub.cancel_at_period_end ? "Access ends" : "Renews"} {new Date(sub.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            </div>
                        )}
                    </div>
                )}

                {/* Card on file — ALWAYS visible so members can add a card
                    without subscribing or buying anything. Coach charges
                    packs/subscriptions against this card later. */}
                <div data-testid="card-on-file-panel" className="mb-8 border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6">
                    <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <h2 className="text-[11px] tracking-[0.25em] uppercase text-brand-red mb-1 inline-flex items-center gap-2">
                                <CreditCard className="w-3.5 h-3.5" /> Card on file
                            </h2>
                            {billing?.has_payment_method ? (
                                <>
                                    <p className="text-sm text-white font-medium">Card saved ✓</p>
                                    <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                                        Your coach can charge packs or activate membership to this card.
                                        Update or remove it anytime via Stripe's secure billing portal.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-white font-medium">No card on file yet</p>
                                    <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                                        Add a card so your coach can charge packs or activate your membership when you're ready. No charge today — this just saves the card securely with Stripe.
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="shrink-0">
                            {billing?.has_payment_method ? (
                                <button
                                    type="button"
                                    data-testid="manage-card-btn"
                                    onClick={openPortal}
                                    className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:text-white inline-flex items-center gap-1.5"
                                >
                                    <CreditCard className="w-3.5 h-3.5" />
                                    Update card
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    data-testid="add-card-btn"
                                    onClick={addCard}
                                    disabled={checkoutBusy === "setup-card"}
                                    className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 bg-brand-red text-white border border-brand-red hover:bg-brand-red/90 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {checkoutBusy === "setup-card"
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <CreditCard className="w-3.5 h-3.5" />}
                                    Add card on file
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Active credits widget */}
                {credits.active_count > 0 && (
                    <div data-testid="active-credits" className="border border-brand-navy-700 bg-brand-navy-950/40 rounded-2xl p-6 mb-8">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <div className="text-[11px] tracking-wider uppercase text-brand-navy-200 inline-flex items-center gap-1"><Ticket className="w-3 h-3" /> Session credits</div>
                                <div className="font-display text-4xl tracking-wider mt-1">{credits.total_remaining} <span className="text-base text-zinc-300">remaining</span></div>
                            </div>
                        </div>
                        <ul className="text-sm divide-y divide-zinc-800">
                            {credits.credits.filter(c => c.status === "active").map((c) => (
                                <li key={c.credit_id} className="py-2 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="truncate">{c.pack_name}</div>
                                        <div className="text-xs text-zinc-300">
                                            {c.remaining_sessions} of {c.total_sessions} left
                                            {c.expires_at && <> · expires {new Date(c.expires_at).toLocaleDateString()}</>}
                                        </div>
                                    </div>
                                    <span className="text-xs text-zinc-300">{new Date(c.purchased_at).toLocaleDateString()}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Commitments widget */}
                {commitments.length > 0 && (
                    <div data-testid="my-commitments" className="border border-emerald-900/50 bg-emerald-950/10 rounded-2xl p-6 mb-8">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <div className="text-[11px] tracking-wider uppercase text-emerald-300 inline-flex items-center gap-1"><Award className="w-3 h-3" /> Membership commitments</div>
                                <div className="font-display text-2xl tracking-wider mt-1 text-white">{commitments.filter(c => c.status === "active").length} active</div>
                            </div>
                        </div>
                        <ul className="text-sm divide-y divide-zinc-800">
                            {commitments.map((c) => {
                                const statusColor = c.status === "active" ? "text-emerald-400" : c.status === "pending" ? "text-amber-300" : c.status === "completed" ? "text-zinc-400" : "text-red-400";
                                return (
                                    <li key={c.commitment_id} data-testid={`my-commitment-${c.commitment_id}`} className="py-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="truncate text-white">{c.display_name}</div>
                                            <div className="text-xs text-zinc-400 mt-0.5 flex flex-wrap gap-x-2">
                                                <span>{c.session_count}-session · {c.commitment_weeks}wk</span>
                                                {c.commitment_started_at && <span>· started {new Date(c.commitment_started_at).toLocaleDateString()}</span>}
                                                {c.commitment_ends_at && <span>· ends {new Date(c.commitment_ends_at).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`text-[10px] uppercase tracking-[0.15em] ${statusColor}`}>{c.status}</span>
                                            {c.status === "active" && (
                                                <CancelSubscriptionButton
                                                    commitment={c}
                                                    onCancelled={load}
                                                />
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        <p className="text-[11px] text-emerald-200/60 mt-3">Pending commitments start automatically the first day you check in.</p>
                    </div>
                )}

                {/* Rollover credits widget */}
                {rollover.credits.length > 0 && (
                    <div data-testid="my-rollover" className="border border-amber-900/50 bg-amber-950/10 rounded-2xl p-6 mb-8">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <div className="text-[11px] tracking-wider uppercase text-amber-300 inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> Rollover credits</div>
                                <div className="font-display text-4xl tracking-wider mt-1 text-white">{formatMoney(rollover.available_cents)} <span className="text-base text-zinc-400">available</span></div>
                            </div>
                        </div>
                        <ul className="text-sm divide-y divide-zinc-800">
                            {rollover.credits.map((r) => (
                                <li key={r.rollover_credit_id} className="py-2 flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-white">{formatMoney(r.amount_cents)} <span className="text-zinc-400 text-xs">· {r.unused_sessions} unused × 85%</span></div>
                                        <div className="text-[11px] text-zinc-500">Banked {new Date(r.created_at).toLocaleDateString()}{r.expires_at && <> · expires {new Date(r.expires_at).toLocaleDateString()}</>}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-[0.15em] ${r.status === "available" ? "text-emerald-400" : "text-zinc-500"}`}>{r.status}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-amber-200/60 mt-3">Credits auto-apply at your next renewal.</p>
                    </div>
                )}

                {/* New purchases route through your coach — parent-side
                    checkout is intentionally disabled (2026-06-01 policy).
                    Existing card-on-file stays usable via Stripe Portal so
                    parents can still cancel or update their card. */}
                {!hasActiveSub && (
                    <div data-testid="coach-driven-purchases" className="mb-10 border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6">
                        <h2 className="text-[11px] tracking-[0.25em] uppercase text-brand-red mb-2 inline-flex items-center gap-2">
                            <Layers className="w-3.5 h-3.5" /> Need a new pack or membership?
                        </h2>
                        <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                            All new memberships and session packs are set up by your coach so we can match the right plan to your goals and pricing tier. Reach out and they'll text or email you a checkout link, or charge your card on file.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to="/dashboard?tab=messages"
                                data-testid="message-coach-btn"
                                className="text-xs tracking-[0.15em] uppercase px-3 py-2 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors"
                            >
                                Message coach
                            </Link>
                            <a
                                href="mailto:sachinm@maxsc.net?subject=Pack%20or%20membership%20question"
                                data-testid="email-coach-btn"
                                className="text-xs tracking-[0.15em] uppercase px-3 py-2 border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:text-white transition-colors"
                            >
                                Email coach
                            </a>
                        </div>
                    </div>
                )}

                {/* Unified charge history — packs, ad-hoc, subscription invoices */}
                {charges.length > 0 && (
                    <div data-testid="charge-history" className="mb-10">
                        <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200 mb-4 inline-flex items-center gap-2">
                            <Receipt className="w-3.5 h-3.5" /> Charge history
                        </h2>
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-900/50 text-[10px] tracking-wider uppercase text-zinc-300">
                                    <tr>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-left px-4 py-3">Description</th>
                                        <th className="text-left px-4 py-3">Type</th>
                                        <th className="text-left px-4 py-3">Amount</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-right px-4 py-3">Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {charges.map((c, idx) => {
                                        const kindLabel = c.kind === "pack" ? "Session pack" : c.kind === "ad_hoc" ? "One-off charge" : "Subscription";
                                        const kindColor = c.kind === "pack" ? "text-brand-red" : c.kind === "ad_hoc" ? "text-amber-400" : "text-brand-navy-300";
                                        const statusColor =
                                            c.status === "paid" || c.status === "succeeded" || c.status === "active" ? "text-emerald-400"
                                            : c.status === "open" ? "text-amber-400"
                                            : c.status === "void" || c.status === "canceled" || c.status === "uncollectible" ? "text-zinc-400"
                                            : "text-zinc-300";
                                        const rowKey = c.stripe_payment_intent_id || `${c.kind}-${c.occurred_at}-${idx}`;
                                        return (
                                            <tr key={rowKey} data-testid={`charge-row-${c.kind}-${idx}`} className="border-t border-zinc-800">
                                                <td className="px-4 py-3 text-zinc-200 whitespace-nowrap">
                                                    {c.occurred_at ? new Date(c.occurred_at).toLocaleDateString() : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-white">
                                                    {c.label}
                                                    {c.charged_by_name && (
                                                        <span className="block text-[10px] text-zinc-400 mt-0.5">by {c.charged_by_name}</span>
                                                    )}
                                                    {c.is_simulated && (
                                                        <span className="ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 border border-zinc-700 text-zinc-400">DEMO</span>
                                                    )}
                                                </td>
                                                <td className={`px-4 py-3 text-[10px] tracking-wider uppercase ${kindColor}`}>{kindLabel}</td>
                                                <td className="px-4 py-3 text-white">{formatMoney(c.amount_cents, c.currency)}</td>
                                                <td className={`px-4 py-3 text-[10px] tracking-wider uppercase ${statusColor}`}>{c.status}</td>
                                                <td className="px-4 py-3 text-right">
                                                    {c.receipt_url ? (
                                                        <a href={c.receipt_url} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white inline-flex items-center gap-1 text-xs">
                                                            View <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    ) : (
                                                        <span className="text-[10px] text-zinc-500">Emailed</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-3">
                            One-off and pack charges send a Stripe receipt to your email on file. Subscription invoices link to a hosted PDF.
                        </p>
                    </div>
                )}

                {!sub && plans.length === 0 && (
                    <div className="mt-6 text-sm text-zinc-300 inline-flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Plans are being set up. Reach out to your coach if this looks wrong.
                    </div>
                )}
            </div>
        </div>
    );
}
